// src/store/slices/agentsSlice.js
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import api from "../../services/AxiosInstance";

export const fetchAgentsThunk = createAsyncThunk(
  "agents/fetchAll",
  async (_, { rejectWithValue }) => {
    try {
      const res = await api.get("/api/agents");
      if (!res.data?.success) return rejectWithValue(res.data?.message || "Failed");
      return res.data?.data || [];
    } catch (err) {
      return rejectWithValue(err?.response?.data?.message || err.message || "Server error");
    }
  }
);

export const createAgentThunk = createAsyncThunk(
  "agents/create",
  async (payload, { rejectWithValue, dispatch }) => {
    try {
      const res = await api.post("/api/agents", payload);
      if (!res.data?.success) return rejectWithValue(res.data?.message || "Failed");
      dispatch(fetchAgentsThunk());
      return true;
    } catch (err) {
      return rejectWithValue(err?.response?.data?.message || err.message || "Server error");
    }
  }
);

export const updateAgentThunk = createAsyncThunk(
  "agents/update",
  async ({ agent_id, ...payload }, { rejectWithValue, dispatch }) => {
    try {
      const res = await api.put(`/api/agents/${agent_id}`, payload);
      if (!res.data?.success) return rejectWithValue(res.data?.message || "Failed");
      dispatch(fetchAgentsThunk());
      return true;
    } catch (err) {
      return rejectWithValue(err?.response?.data?.message || err.message || "Server error");
    }
  }
);

export const deleteAgentThunk = createAsyncThunk(
  "agents/delete",
  async ({ agent_id }, { rejectWithValue, dispatch }) => {
    try {
      const res = await api.delete(`/api/agents/${agent_id}`);
      if (!res.data?.success) return rejectWithValue(res.data?.message || "Failed");
      dispatch(fetchAgentsThunk());
      return true;
    } catch (err) {
      return rejectWithValue(err?.response?.data?.message || err.message || "Server error");
    }
  }
);

const agentsSlice = createSlice({
  name: "agents",
  initialState: {
    list: [],
    loading: false,
    saving: false,
    error: null,
  },
  reducers: {
    clearAgentsError(state) {
      state.error = null;
    },
    clearAgents(state) {
      state.list = [];
      state.loading = false;
      state.saving = false;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // fetch
      .addCase(fetchAgentsThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
        // optional: clear to avoid stale UI while loading
        // state.list = [];
      })
      .addCase(fetchAgentsThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.list = action.payload || [];
      })
      .addCase(fetchAgentsThunk.rejected, (state, action) => {
        state.loading = false;
        state.list = []; // ✅ important: clear stale data
        state.error = action.payload || "Failed to load agents";
      })

      // saving
      .addMatcher(
        (a) =>
          a.type === createAgentThunk.pending.type ||
          a.type === updateAgentThunk.pending.type ||
          a.type === deleteAgentThunk.pending.type,
        (state) => {
          state.saving = true;
          state.error = null;
        }
      )
      .addMatcher(
        (a) =>
          a.type === createAgentThunk.fulfilled.type ||
          a.type === updateAgentThunk.fulfilled.type ||
          a.type === deleteAgentThunk.fulfilled.type,
        (state) => {
          state.saving = false;
        }
      )
      .addMatcher(
        (a) =>
          a.type === createAgentThunk.rejected.type ||
          a.type === updateAgentThunk.rejected.type ||
          a.type === deleteAgentThunk.rejected.type,
        (state, action) => {
          state.saving = false;
          state.error = action.payload || "Operation failed";
        }
      );
  },
});

export const { clearAgentsError, clearAgents } = agentsSlice.actions;
export default agentsSlice.reducer;
